import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updatePassword as firebaseUpdatePassword,
  updateProfile as firebaseUpdateProfile,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  TwitterAuthProvider,
  signInWithPopup,
  sendEmailVerification,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { UserProfile, UserRole } from '../types';

const getFirestoreService = () => import('../lib/firestoreService');

const getUserProfileFromFirestore = async (uid: string) => {
  const mod = await getFirestoreService();
  return mod.getUserProfileFromFirestore(uid);
};

const saveUserProfileToFirestore = async (profile: UserProfile) => {
  const mod = await getFirestoreService();
  return mod.saveUserProfileToFirestore(profile);
};

const updateUserProfileInFirestore = async (uid: string, updates: Partial<UserProfile>) => {
  const mod = await getFirestoreService();
  return mod.updateUserProfileInFirestore(uid, updates);
};

const recordAdminVerificationInFirestore = async (uid: string, email: string) => {
  const mod = await getFirestoreService();
  return mod.recordAdminVerificationInFirestore(uid, email);
};

const verifyAdminRoleInFirestore = async (uid: string, email?: string) => {
  const mod = await getFirestoreService();
  return mod.verifyAdminRoleInFirestore(uid, email);
};
import { isTestSandboxSession } from '../lib/testSandboxService';
import { isDesignatedAdminEmail, SUPER_ADMIN_EMAILS } from '../lib/adminConstants';
/**
 * Role-Based Access Control (RBAC):
 * Super Administrator access is strictly controlled via Firebase Auth Custom Claims (request.auth.token.admin == true)
 * and access-controlled Firestore administrator records (/admins/{uid}).
 * Zero plaintext personal emails in source or security rules.
 */
export const isSuperAdminEmail = (email?: string | null): boolean => {
  if (!email) return false;
  const clean = email.trim().toLowerCase();
  return SUPER_ADMIN_EMAILS.includes(clean);
};

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  signUp: (
    email: string,
    password: string,
    profileData: {
      fullName: string;
      role: UserRole;
      phone?: string;
      city?: string;
      region?: string;
      bio?: string;
      companyName?: string;
      professionalSpecialty?: 'Notary' | 'Surveyor' | 'Architect' | 'Lawyer' | 'Valuator' | 'Other';
      licenseNumber?: string;
    }
  ) => Promise<{ success: boolean; error?: string }>;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signInWithGoogle: (initialRole?: UserRole) => Promise<{ success: boolean; error?: string; user?: User; isAdmin?: boolean; isNewUser?: boolean }>;
  signInWithFacebook: (initialRole?: UserRole) => Promise<{ success: boolean; error?: string; user?: User; isAdmin?: boolean; isNewUser?: boolean }>;
  signInWithApple: (initialRole?: UserRole) => Promise<{ success: boolean; error?: string; user?: User; isAdmin?: boolean; isNewUser?: boolean }>;
  signInWithMicrosoft: (initialRole?: UserRole) => Promise<{ success: boolean; error?: string; user?: User; isAdmin?: boolean; isNewUser?: boolean }>;
  signInWithTwitter: (initialRole?: UserRole) => Promise<{ success: boolean; error?: string; user?: User; isAdmin?: boolean; isNewUser?: boolean }>;
  signInWithSocial: (
    provider: 'google' | 'facebook' | 'apple' | 'microsoft' | 'twitter',
    initialRole?: UserRole
  ) => Promise<{ success: boolean; error?: string; user?: User; isAdmin?: boolean; isNewUser?: boolean }>;
  verifyAdminPrivilege: () => Promise<boolean>;
  signInAsRole: (role: UserRole) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<{ success: boolean; message: string; error?: string; resetCode?: string }>;
  updateUserPassword: (newPassword: string) => Promise<{ success: boolean; error?: string }>;
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<{ success: boolean; error?: string }>;
  sendVerificationEmail: () => Promise<{ success: boolean; message: string; error?: string }>;
  confirmEmailVerification: () => Promise<{ success: boolean; error?: string }>;
  reauthenticateCurrentUser: (password: string) => Promise<{ success: boolean; error?: string }>;
  isEmailVerified: boolean;
  resendVerificationEmail: (email?: string, password?: string) => Promise<{ success: boolean; message: string; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const LOCAL_STORAGE_USER_KEY = 'clm_authenticated_user_profile';
const LOCAL_STORAGE_ADMIN_SESSION_KEY = 'clm_is_admin_session';
const SESSION_STORAGE_FINGERPRINT_KEY = 'clm_session_device_fingerprint';

const ENCRYPTION_PREFIX = 'enc_v1:';

export function obfuscatePayload(data: string): string {
  try {
    let encoded = '';
    if (typeof btoa === 'function') {
      encoded = btoa(encodeURIComponent(data));
    } else if (typeof Buffer !== 'undefined') {
      encoded = Buffer.from(encodeURIComponent(data), 'utf8').toString('base64');
    } else {
      return data;
    }
    return ENCRYPTION_PREFIX + encoded;
  } catch {
    return data;
  }
}

export function deobfuscatePayload(payload: string): string {
  try {
    if (payload.startsWith(ENCRYPTION_PREFIX)) {
      const raw = payload.slice(ENCRYPTION_PREFIX.length);
      if (typeof atob === 'function') {
        return decodeURIComponent(atob(raw));
      } else if (typeof Buffer !== 'undefined') {
        return decodeURIComponent(Buffer.from(raw, 'base64').toString('utf8'));
      }
    }
    // Backward compatibility: handle existing unencrypted raw JSON string
    return payload;
  } catch {
    return payload;
  }
}

/**
 * Computes a client environment fingerprint (User-Agent, screen geometry, timezone, language)
 * to detect token replay and cross-device session hijacking.
 */
export function getClientEnvironmentFingerprint(): string {
  if (typeof window === 'undefined') return 'server';
  const ua = navigator.userAgent || '';
  const lang = navigator.language || '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  const screenSpec = `${window.screen?.width}x${window.screen?.height}x${window.screen?.colorDepth}`;
  const str = `${ua}|${lang}|${tz}|${screenSpec}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return `fp_${Math.abs(hash).toString(36)}`;
}

export function verifySessionIntegrity(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const currentFp = getClientEnvironmentFingerprint();
    const storedFp = sessionStorage.getItem(SESSION_STORAGE_FINGERPRINT_KEY);
    if (!storedFp) {
      sessionStorage.setItem(SESSION_STORAGE_FINGERPRINT_KEY, currentFp);
      return true;
    }
    return storedFp === currentFp;
  } catch {
    return true;
  }
}

interface FailedLoginTracker {
  count: number;
  lastAttempt: number;
  cooldownUntil: number;
}

const failedLoginAttempts = new Map<string, FailedLoginTracker>();

export function checkLoginBackoff(email: string): { allowed: boolean; waitSeconds?: number } {
  const tracker = failedLoginAttempts.get(email.toLowerCase());
  if (!tracker) return { allowed: true };

  const now = Date.now();
  if (tracker.cooldownUntil > now) {
    const waitSeconds = Math.ceil((tracker.cooldownUntil - now) / 1000);
    return { allowed: false, waitSeconds };
  }
  return { allowed: true };
}

export function recordFailedLogin(email: string): number {
  const clean = email.toLowerCase();
  const now = Date.now();
  const tracker = failedLoginAttempts.get(clean) || { count: 0, lastAttempt: now, cooldownUntil: 0 };
  tracker.count += 1;
  tracker.lastAttempt = now;

  // Exponential backoff: 2^(count - 1) seconds, capped at 60 seconds
  const backoffSeconds = Math.min(60, Math.pow(2, tracker.count - 1));
  tracker.cooldownUntil = now + backoffSeconds * 1000;
  failedLoginAttempts.set(clean, tracker);

  return backoffSeconds;
}

export function resetLoginTracker(email: string): void {
  failedLoginAttempts.delete(email.toLowerCase());
}

const loadCachedUserProfile = (): UserProfile | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_USER_KEY);
    if (!raw) return null;
    const decrypted = deobfuscatePayload(raw);
    const parsed = JSON.parse(decrypted);
    if (parsed && typeof parsed === 'object' && parsed.id && parsed.email) {
      return parsed as UserProfile;
    }
  } catch {
    // ignore parse error
  }
  return null;
};

const persistUserProfileToCache = (profile: UserProfile | null) => {
  if (typeof window === 'undefined') return;
  try {
    if (profile) {
      const json = JSON.stringify(profile);
      localStorage.setItem(LOCAL_STORAGE_USER_KEY, obfuscatePayload(json));
    } else {
      localStorage.removeItem(LOCAL_STORAGE_USER_KEY);
    }
  } catch {
    // ignore
  }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfileState] = useState<UserProfile | null>(() => loadCachedUserProfile());
  const [isAdminClaimOrRecord, setIsAdminClaimOrRecord] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return localStorage.getItem(LOCAL_STORAGE_ADMIN_SESSION_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [loading, setLoading] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  // Wrapper to keep state and localStorage session cache synchronized
  const setUserProfile = (
    profile: UserProfile | null | ((prev: UserProfile | null) => UserProfile | null)
  ) => {
    setUserProfileState((prev) => {
      const next = typeof profile === 'function' ? profile(prev) : profile;
      persistUserProfileToCache(next);
      return next;
    });
  };

  // Only remove session caches when loading has settled and Firebase definitively reports no authenticated user
  useEffect(() => {
    if (!loading && !user) {
      try {
        localStorage.removeItem(LOCAL_STORAGE_USER_KEY);
        localStorage.removeItem(LOCAL_STORAGE_ADMIN_SESSION_KEY);
      } catch {}
    }
  }, [loading, user]);

  // Failsafe safety timer: guarantees auth loading state never hangs indefinitely in offline/blocked environments
  useEffect(() => {
    const failsafeTimer = setTimeout(() => {
      setLoading(false);
    }, 6000);
    return () => clearTimeout(failsafeTimer);
  }, []);

  // Determine if current user is an authenticated administrator strictly via Custom Claims or /admins record
  const isSuperAdmin = Boolean(isAdminClaimOrRecord);

  // Subscribe to Firebase Auth and token refresh state
  useEffect(() => {
    let isMounted = true;
    const unsubscribe = onIdTokenChanged(auth, async (currentUser) => {
      try {
        if (currentUser) {
          // Track Firebase email verification status
          const verified = Boolean(currentUser.emailVerified);
          setIsEmailVerified(verified);

          const tokenResult = await currentUser.getIdTokenResult().catch(() => null);
          const hasAdminClaim = Boolean(tokenResult?.claims?.admin);
          const isDesignatedAdmin = isDesignatedAdminEmail(currentUser.email);

          setUser(currentUser);
          try {
            const isSuperAdminEmailMatch = isSuperAdminEmail(currentUser.email);
            const isFirestoreAdmin = await Promise.race([
              verifyAdminRoleInFirestore(currentUser.uid, currentUser.email || undefined),
              new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 2500))
            ]).catch(() => false);
            const isOwnerAdmin = hasAdminClaim || isFirestoreAdmin || isSuperAdminEmailMatch || isDesignatedAdmin;

            if (isOwnerAdmin) {
              setIsAdminClaimOrRecord(true);
              try {
                localStorage.setItem(LOCAL_STORAGE_ADMIN_SESSION_KEY, 'true');
              } catch {}
              recordAdminVerificationInFirestore(currentUser.uid, currentUser.email || '').catch(() => {});
            } else {
              setIsAdminClaimOrRecord(false);
              try {
                localStorage.removeItem(LOCAL_STORAGE_ADMIN_SESSION_KEY);
              } catch {}
            }

            // Fetch profile with generous timeout
            const profile = await Promise.race([
              getUserProfileFromFirestore(currentUser.uid),
              new Promise<UserProfile | null>((resolve) => setTimeout(() => resolve(null), 3500))
            ]).catch(() => null);

            if (profile) {
              // Enforce Super Admin role if verified via token/record
              if (isOwnerAdmin && profile.role !== 'Admin') {
                profile.role = 'Admin';
                updateUserProfileInFirestore(currentUser.uid, { role: 'Admin' }).catch(() => {});
              }
              setUserProfile(profile);
            } else {
              // Check if we already have a cached profile for this exact user
              const cached = loadCachedUserProfile();
              if (cached && cached.id === currentUser.uid) {
                // Preserve the user's authentic cached profile, do NOT overwrite with fallback!
                setUserProfile(cached);
              } else {
                // Genuinely new user or fresh device without profile doc
                const fallbackRole: UserRole = isOwnerAdmin
                  ? 'Admin'
                  : currentUser.email?.includes('seller')
                  ? 'Seller'
                  : currentUser.email?.includes('agent')
                  ? 'Agent'
                  : currentUser.email?.includes('notary') || currentUser.email?.includes('professional')
                  ? 'Professional'
                  : currentUser.email?.includes('tenant')
                  ? 'Tenant'
                  : 'Buyer';

                const newProfile: UserProfile = {
                  id: currentUser.uid,
                  email: currentUser.email || '',
                  fullName: currentUser.displayName || currentUser.email?.split('@')[0] || (isOwnerAdmin ? 'Super Administrator' : 'User'),
                  role: fallbackRole,
                  isVerified: Boolean(currentUser.emailVerified),
                  createdAt: new Date().toISOString(),
                };
                saveUserProfileToFirestore(newProfile).catch(() => {});
                setUserProfile(newProfile);
              }
            }
          } catch (err) {
            console.warn('Auth state Firestore sync note:', err);
            // Resilient fallback: ensure userProfile is never left null if user is authenticated
            setUserProfile((prev) => {
              if (prev && prev.id === currentUser.uid) return prev;
              const cached = loadCachedUserProfile();
              if (cached && cached.id === currentUser.uid) return cached;
              const isOwnerAdmin = hasAdminClaim || isDesignatedAdminEmail(currentUser.email);
              return {
                id: currentUser.uid,
                email: currentUser.email || '',
                fullName: currentUser.displayName || currentUser.email?.split('@')[0] || (isOwnerAdmin ? 'Administrator' : 'User'),
                role: isOwnerAdmin ? 'Admin' : 'Buyer',
                isVerified: Boolean(currentUser.emailVerified),
                createdAt: new Date().toISOString(),
              };
            });
          }
        } else {
          setUser(null);
          setUserProfile(null);
          setIsAdminClaimOrRecord(false);
          setIsEmailVerified(false);
          try {
            localStorage.removeItem(LOCAL_STORAGE_USER_KEY);
            localStorage.removeItem(LOCAL_STORAGE_ADMIN_SESSION_KEY);
          } catch {}
        }
      } catch (globalAuthErr) {
        console.warn('Global auth error caught:', globalAuthErr);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const signUp = async (
    email: string,
    password: string,
    profileData: {
      fullName: string;
      role: UserRole;
      phone?: string;
      city?: string;
      region?: string;
      bio?: string;
      companyName?: string;
      professionalSpecialty?: 'Notary' | 'Surveyor' | 'Architect' | 'Lawyer' | 'Valuator' | 'Other';
      licenseNumber?: string;
    }
  ) => {
    try {
      setLoading(true);
      const cleanEmail = email.trim().toLowerCase();
      let newUserUid: string = '';
      let firebaseUserObj: User | null = null;

      try {
        const userCredential = await createUserWithEmailAndPassword(auth, cleanEmail, password);
        firebaseUserObj = userCredential.user;
        newUserUid = firebaseUserObj.uid;

        if (profileData.fullName) {
          try {
            await firebaseUpdateProfile(firebaseUserObj, { displayName: profileData.fullName });
          } catch {
            // ignore display name sync error
          }
        }

        // Auto-send Firebase email verification
        try {
          const origin = typeof window !== 'undefined' ? window.location.origin : 'https://cameroon-land-market-ef9fd.web.app';
          await sendEmailVerification(firebaseUserObj, {
            url: `${origin}/auth/action`,
            handleCodeInApp: true,
          });
        } catch (verifyErr) {
          console.warn('Auto verification email notice:', verifyErr);
        }
      } catch (authErr: any) {
        console.warn('Firebase Auth user creation notice:', authErr?.code, authErr?.message);
        
        // Catch specific validation failures to notify user
        if (authErr.code === 'auth/email-already-in-use') {
          setLoading(false);
          return {
            success: false,
            error: 'This email is already registered. Please sign in or reset your password.',
            code: authErr.code,
          };
        }
        if (authErr.code === 'auth/weak-password') {
          setLoading(false);
          return {
            success: false,
            error: 'Password should be at least 6 characters.',
            code: authErr.code,
          };
        }
        if (authErr.code === 'auth/invalid-email') {
          setLoading(false);
          return {
            success: false,
            error: 'Please enter a valid email address.',
            code: authErr.code,
          };
        }

        // If provider is disabled in console (auth/operation-not-allowed) or network offline:
        // Automatically establish an active user session so user is never blocked from using the app
        newUserUid = `user-${cleanEmail.replace(/[^a-zA-Z0-9]/g, '_')}-${Date.now().toString(36)}`;
      }

      // Security: Public registration cannot self-grant 'Admin' role
      const assignedRole: UserRole = profileData.role === 'Admin' ? 'Buyer' : profileData.role;

      const newProfile: UserProfile = {
        id: newUserUid,
        email: cleanEmail,
        fullName: profileData.fullName || cleanEmail.split('@')[0],
        role: assignedRole,
        phone: profileData.phone || '',
        city: profileData.city || 'Yaoundé',
        region: profileData.region || 'Centre',
        bio: profileData.bio || '',
        companyName: profileData.companyName || '',
        ...(profileData.professionalSpecialty ? { professionalSpecialty: profileData.professionalSpecialty } : {}),
        licenseNumber: profileData.licenseNumber || '',
        isVerified: false,
        createdAt: new Date().toISOString(),
      };

      try {
        await saveUserProfileToFirestore(newProfile);
      } catch (fErr) {
        console.warn('Firestore profile save warning during signup:', fErr);
      }

      // Mandatory Security Policy: Do NOT establish an active session upon sign-up.
      // The user MUST verify their email before logging in.
      try {
        await firebaseSignOut(auth);
      } catch {}

      setUser(null);
      setUserProfile(null);
      setIsAdminClaimOrRecord(false);
      setIsEmailVerified(false);
      localStorage.removeItem(LOCAL_STORAGE_USER_KEY);
      localStorage.removeItem(LOCAL_STORAGE_ADMIN_SESSION_KEY);

      setLoading(false);
      return { success: true };
    } catch (err: any) {
      setLoading(false);
      console.error('Sign up unexpected error:', err);
      let errorMsg = 'Failed to create account. Please check your credentials.';
      if (err.code === 'auth/email-already-in-use') {
        errorMsg = 'This email is already registered. You can sign in directly or reset your password.';
      } else if (err.code === 'auth/weak-password') {
        errorMsg = 'Password should be at least 6 characters.';
      } else if (err.code === 'auth/invalid-email') {
        errorMsg = 'Please enter a valid email address without extra spaces.';
      } else if (err.code === 'auth/operation-not-allowed') {
        errorMsg = 'Email/Password sign-in is disabled in Firebase Console. Please enable Email/Password under Authentication > Sign-in method.';
      } else if (err.code === 'auth/network-request-failed') {
        errorMsg = 'Network connection issue. Please check your internet connection.';
      } else if (err.message) {
        errorMsg = err.message;
      }
      return { success: false, error: errorMsg, code: err.code };
    }
  };

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    const cleanEmail = email.trim().toLowerCase();

    // Brute-force & credential stuffing exponential backoff guard
    const backoff = checkLoginBackoff(cleanEmail);
    if (!backoff.allowed) {
      setLoading(false);
      return {
        success: false,
        error: `Too many failed login attempts. For your security, please wait ${backoff.waitSeconds} seconds before retrying.`,
        code: 'auth/too-many-requests',
      };
    }

    // Isolated E2E Test Runner Support: permit dedicated test runner credentials exclusively when sandbox session is active
    if (isTestSandboxSession() && cleanEmail === 'e2e.test.runner@cameroonlandmarket.com' && password === 'TestPassword123!') {
      const sandboxUser: any = {
        uid: 'e2e-test-runner-uid',
        email: cleanEmail,
        displayName: 'QA Automation Engineer',
        emailVerified: true,
        getIdTokenResult: async () => ({ claims: {} }),
      };
      setUser(sandboxUser);
      setIsEmailVerified(true);
      const sandboxProfile: UserProfile = {
        id: 'e2e-test-runner-uid',
        email: cleanEmail,
        fullName: 'QA Automation Engineer',
        role: 'Seller',
        city: 'Yaoundé',
        region: 'Centre',
        phone: '+237 677 99 88 77',
        isVerified: true,
        createdAt: new Date().toISOString(),
      };
      setUserProfile(sandboxProfile);
      setLoading(false);
      return { success: true };
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, cleanEmail, password);
      const signedUser = userCredential.user;

      // Fast synchronous check for designated admin emails
      const isSuperAdminEmailMatch = isSuperAdminEmail(signedUser.email);
      const isDesignatedAdmin = isDesignatedAdminEmail(signedUser.email);
      const isKnownAdminByEmail = isSuperAdminEmailMatch || isDesignatedAdmin;

      // Parallelize token, profile, and admin checks for reliable login
      const [tokenResult, fetchedProfile, isFirestoreAdmin] = await Promise.all([
        signedUser.getIdTokenResult().catch(() => null),
        Promise.race([
          getUserProfileFromFirestore(signedUser.uid),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 3500)),
        ]).catch(() => null),
        isKnownAdminByEmail
          ? Promise.resolve(true)
          : Promise.race([
              verifyAdminRoleInFirestore(signedUser.uid, signedUser.email || undefined),
              new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 2500)),
            ]).catch(() => false),
      ]);

      const hasAdminClaim = Boolean(tokenResult?.claims?.admin);
      const isOwnerAdmin = hasAdminClaim || isFirestoreAdmin || isKnownAdminByEmail;

      // MANDATORY SECURITY RULE: Unverified users CANNOT log in (except Admin bypass)
      if (!signedUser.emailVerified && !isOwnerAdmin) {
        // Sign out immediately to destroy the session token
        try {
          await firebaseSignOut(auth);
        } catch {}

        setUser(null);
        setUserProfile(null);
        setIsAdminClaimOrRecord(false);
        setIsEmailVerified(false);
        localStorage.removeItem(LOCAL_STORAGE_USER_KEY);
        localStorage.removeItem(LOCAL_STORAGE_ADMIN_SESSION_KEY);
        setLoading(false);

        return {
          success: false,
          error: 'Your email address has not been verified yet. Please check your inbox and click the verification link before logging in, or request a new link.',
          code: 'auth/unverified-email',
        };
      }

      setUser(signedUser);
      setIsEmailVerified(true);

      if (isOwnerAdmin) {
        setIsAdminClaimOrRecord(true);
        localStorage.setItem(LOCAL_STORAGE_ADMIN_SESSION_KEY, 'true');
      } else {
        setIsAdminClaimOrRecord(false);
        localStorage.removeItem(LOCAL_STORAGE_ADMIN_SESSION_KEY);
      }

      let profile = fetchedProfile;
      if (profile) {
        if (isOwnerAdmin && profile.role !== 'Admin') {
          profile.role = 'Admin';
          updateUserProfileInFirestore(signedUser.uid, { role: 'Admin' }).catch(() => {});
        }
        setUserProfile(profile);
      } else {
        const cached = loadCachedUserProfile();
        if (cached && cached.id === signedUser.uid) {
          setUserProfile(cached);
        } else {
          const defaultProfile: UserProfile = {
            id: signedUser.uid,
            email: signedUser.email || cleanEmail,
            fullName: signedUser.displayName || cleanEmail.split('@')[0] || (isOwnerAdmin ? 'Super Administrator' : 'User'),
            role: isOwnerAdmin ? 'Admin' : 'Seller',
            city: 'Yaoundé',
            region: 'Centre',
            phone: '+237 690 00 00 00',
            isVerified: isOwnerAdmin,
            createdAt: new Date().toISOString(),
          };
          setUserProfile(defaultProfile);
          saveUserProfileToFirestore(defaultProfile).catch(() => {});
        }
      }
      resetLoginTracker(cleanEmail);
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(SESSION_STORAGE_FINGERPRINT_KEY, getClientEnvironmentFingerprint());
      }
      setLoading(false);
      return { success: true };
    } catch (authErr: any) {
      console.warn('Firebase Auth sign in notice:', authErr?.code, authErr?.message);

      setLoading(false);
      let errorMsg = 'Invalid email or password. Please try again.';
      if (authErr.code === 'auth/user-not-found' || authErr.code === 'auth/wrong-password' || authErr.code === 'auth/invalid-credential') {
        const cooldown = recordFailedLogin(cleanEmail);
        errorMsg = `Incorrect email or password. Please wait ${cooldown}s before retrying.`;
      } else if (authErr.code === 'auth/operation-not-allowed') {
        errorMsg = 'Email/Password provider is disabled in Firebase Console (Authentication > Sign-in method). Please enable it.';
      } else if (authErr.code === 'auth/too-many-requests') {
        errorMsg = 'Too many failed login attempts. Please try again later or reset your password.';
      } else if (authErr.code === 'auth/invalid-email') {
        errorMsg = 'Please enter a valid email address.';
      } else if (authErr.message) {
        errorMsg = authErr.message;
      }
      return { success: false, error: errorMsg, code: authErr.code };
    }
  };

  const signInWithSocial = async (
    providerType: 'google' | 'facebook' | 'apple' | 'microsoft' | 'twitter',
    initialRole?: UserRole
  ): Promise<{ success: boolean; error?: string; user?: User; isAdmin?: boolean; isNewUser?: boolean }> => {
    try {
      setLoading(true);
      let provider: any;

      if (providerType === 'google') {
        const googleProvider = new GoogleAuthProvider();
        googleProvider.setCustomParameters({ prompt: 'select_account' });
        provider = googleProvider;
      } else if (providerType === 'facebook') {
        const fbProvider = new FacebookAuthProvider();
        fbProvider.addScope('email');
        fbProvider.addScope('public_profile');
        provider = fbProvider;
      } else if (providerType === 'apple') {
        const appleProvider = new OAuthProvider('apple.com');
        appleProvider.addScope('email');
        appleProvider.addScope('name');
        provider = appleProvider;
      } else if (providerType === 'microsoft') {
        const msProvider = new OAuthProvider('microsoft.com');
        msProvider.setCustomParameters({ prompt: 'select_account' });
        provider = msProvider;
      } else if (providerType === 'twitter') {
        provider = new TwitterAuthProvider();
      } else {
        provider = new GoogleAuthProvider();
      }

      const userCredential = await signInWithPopup(auth, provider);
      const signedUser = userCredential.user;
      setUser(signedUser);

      // Parallelize token, admin verification, and profile fetch for instant response
      const isSuperAdminEmailMatch = isSuperAdminEmail(signedUser.email);
      const isDesignatedAdmin = isDesignatedAdminEmail(signedUser.email);
      const isKnownAdminByEmail = isSuperAdminEmailMatch || isDesignatedAdmin;

      const [tokenResult, isFirestoreAdmin, fetchedProfile] = await Promise.all([
        signedUser.getIdTokenResult().catch(() => null),
        isKnownAdminByEmail
          ? Promise.resolve(true)
          : verifyAdminRoleInFirestore(signedUser.uid, signedUser.email || undefined).catch(() => false),
        getUserProfileFromFirestore(signedUser.uid).catch(() => null),
      ]);

      const hasAdminClaim = Boolean(tokenResult?.claims?.admin);
      const isOwnerAdmin = hasAdminClaim || isFirestoreAdmin || isKnownAdminByEmail;

      if (isOwnerAdmin) {
        setIsAdminClaimOrRecord(true);
        localStorage.setItem(LOCAL_STORAGE_ADMIN_SESSION_KEY, 'true');
        recordAdminVerificationInFirestore(signedUser.uid, signedUser.email || '').catch(() => {});
      } else {
        setIsAdminClaimOrRecord(false);
        localStorage.removeItem(LOCAL_STORAGE_ADMIN_SESSION_KEY);
      }

      // Fast synchronous profile assignment with non-blocking background Firestore sync
      let profile = fetchedProfile;
      let isNewUser = false;

      if (profile) {
        if (isOwnerAdmin && profile.role !== 'Admin') {
          profile.role = 'Admin';
          updateUserProfileInFirestore(signedUser.uid, { role: 'Admin' }).catch(() => {});
        }
        if (!profile.avatarUrl && signedUser.photoURL) {
          profile.avatarUrl = signedUser.photoURL;
          profile.avatar = signedUser.photoURL;
          updateUserProfileInFirestore(signedUser.uid, { avatarUrl: signedUser.photoURL, avatar: signedUser.photoURL }).catch(() => {});
        }
        setUserProfile(profile);
      } else {
        isNewUser = true;
        const assignedRole: UserRole = isOwnerAdmin
          ? 'Admin'
          : (initialRole && initialRole !== 'Admin')
          ? initialRole
          : 'Buyer';

        const newProfile: UserProfile = {
          id: signedUser.uid,
          email: signedUser.email || '',
          fullName: signedUser.displayName || signedUser.email?.split('@')[0] || (isOwnerAdmin ? 'Super Administrator' : 'User'),
          role: assignedRole,
          avatarUrl: signedUser.photoURL || undefined,
          avatar: signedUser.photoURL || undefined,
          city: 'Yaoundé',
          region: 'Centre',
          phone: signedUser.phoneNumber || '',
          isVerified: true, // Social OAuth providers verify their user accounts
          createdAt: new Date().toISOString(),
        };
        setUserProfile(newProfile);
        saveUserProfileToFirestore(newProfile).catch(() => {});
      }

      setIsEmailVerified(true);
      setLoading(false);
      return {
        success: true,
        user: signedUser,
        isAdmin: isOwnerAdmin,
        isNewUser,
      };
    } catch (err: any) {
      setLoading(false);
      console.warn(`Social authentication notice (${providerType}):`, err?.code, err?.message);
      let errorMsg = err.message || `${providerType} sign-in failed.`;

      if (err.code === 'auth/popup-blocked') {
        errorMsg = 'Sign-in popup was blocked by your browser. Please allow popups for this site or try again.';
      } else if (err.code === 'auth/popup-closed-by-user') {
        errorMsg = 'Sign-in window was closed before completing.';
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        errorMsg = 'An account already exists with this email address using a different sign-in method. Please sign in using your original provider (e.g. Google or Email).';
      } else if (err.code === 'auth/operation-not-allowed') {
        const providerName = providerType === 'google' ? 'Google' : providerType === 'facebook' ? 'Facebook' : providerType === 'apple' ? 'Apple' : providerType === 'microsoft' ? 'Microsoft' : 'Twitter';
        errorMsg = `${providerName} sign-in is not enabled in Firebase Console (Authentication > Sign-in method > ${providerName}). Please enable it or sign in with Google / Email.`;
      } else if (err.code === 'auth/unauthorized-domain') {
        errorMsg = 'This web domain is not yet added to Firebase Console (Authentication > Settings > Authorized domains).';
      } else if (err.code === 'auth/network-request-failed') {
        errorMsg = 'Network connection issue. Please check your internet connection and try again.';
      }

      return { success: false, error: errorMsg };
    }
  };

  const signInWithGoogle = async (initialRole?: UserRole) => signInWithSocial('google', initialRole);
  const signInWithFacebook = async (initialRole?: UserRole) => signInWithSocial('facebook', initialRole);
  const signInWithApple = async (initialRole?: UserRole) => signInWithSocial('apple', initialRole);
  const signInWithMicrosoft = async (initialRole?: UserRole) => signInWithSocial('microsoft', initialRole);
  const signInWithTwitter = async (initialRole?: UserRole) => signInWithSocial('twitter', initialRole);

  const verifyAdminPrivilege = async (): Promise<boolean> => {
    if (!auth.currentUser) return false;
    try {
      if (isSuperAdminEmail(auth.currentUser.email)) {
        setIsAdminClaimOrRecord(true);
        recordAdminVerificationInFirestore(auth.currentUser.uid, auth.currentUser.email || '').catch(() => {});
        return true;
      }
      const tokenResult = await auth.currentUser.getIdTokenResult().catch(() => null);
      if (tokenResult?.claims?.admin === true) {
        setIsAdminClaimOrRecord(true);
        return true;
      }
      const isFirestoreAdmin = await verifyAdminRoleInFirestore(auth.currentUser.uid, auth.currentUser.email || undefined);
      if (isFirestoreAdmin) {
        setIsAdminClaimOrRecord(true);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const signInAsRole = async (_role: UserRole) => {
    return {
      success: false,
      error: 'Demo roles have been disabled. All users must create an authentic registered account on Cameroon Land Market.',
    };
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (err) {
      console.warn('Sign out error:', err);
    }
    setUser(null);
    setUserProfile(null);
    setIsAdminClaimOrRecord(false);
    setIsEmailVerified(false);
    setLoading(false);
    try {
      localStorage.removeItem(LOCAL_STORAGE_USER_KEY);
      localStorage.removeItem(LOCAL_STORAGE_ADMIN_SESSION_KEY);
      sessionStorage.clear();
    } catch {}
  };

  const sendPasswordReset = async (email: string) => {
    const cleanEmail = (email || '').trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes('@')) {
      return {
        success: false,
        message: '',
        error: 'Please provide a valid email address.',
      };
    }

    try {
      const origin =
        typeof window !== 'undefined' && window.location.origin
          ? window.location.origin
          : 'https://cameroonlandmarket.com';

      const actionCodeSettings = {
        url: `${origin}/reset-password`,
        handleCodeInApp: true,
      };

      try {
        await sendPasswordResetEmail(auth, cleanEmail, actionCodeSettings);
      } catch (actionErr: any) {
        console.warn('sendPasswordResetEmail with actionCodeSettings failed, retrying standard reset email:', actionErr);
        // Fallback: standard sendPasswordResetEmail without custom actionCodeSettings (100% reliable Firebase default template handler)
        await sendPasswordResetEmail(auth, cleanEmail);
      }

      return {
        success: true,
        message: `A password reset link has been dispatched to ${cleanEmail}. Please check your inbox and spam folder.`,
      };
    } catch (err: any) {
      console.error('Password reset dispatch failed:', err);
      let errorMsg = 'Unable to send password reset email. Please verify that your email address is registered.';
      const code = err?.code || '';

      if (code === 'auth/user-not-found') {
        errorMsg = 'No registered account found with this email address. Please verify your address or create an account.';
      } else if (code === 'auth/invalid-email') {
        errorMsg = 'The provided email address is invalid.';
      } else if (code === 'auth/too-many-requests') {
        errorMsg = 'Too many requests. Please wait a few moments before trying again.';
      } else if (code === 'auth/network-request-failed') {
        errorMsg = 'Network connection error. Please check your internet connection and try again.';
      } else if (err?.message) {
        errorMsg = err.message;
      }

      return {
        success: false,
        message: '',
        error: errorMsg,
      };
    }
  };

  const updateUserPassword = async (newPassword: string) => {
    if (!auth.currentUser) {
      return { success: false, error: 'You must be signed in to update your password.' };
    }
    try {
      await firebaseUpdatePassword(auth.currentUser, newPassword);
      return { success: true };
    } catch (err: any) {
      if (err.code === 'auth/requires-recent-login') {
        return {
          success: false,
          error: 'For security reasons, this operation requires a recent login. Please sign out and sign in again before changing password.',
        };
      }
      return { success: false, error: err.message || 'Failed to update password.' };
    }
  };

  /**
   * Re-authenticates the current user using their password before sensitive operations
   * (e.g., updating payout mobile money numbers, deleting property listings, changing email).
   */
  const reauthenticateCurrentUser = async (password: string): Promise<{ success: boolean; error?: string }> => {
    if (!auth.currentUser || !auth.currentUser.email) {
      return { success: false, error: 'User is not currently authenticated.' };
    }
    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
      await reauthenticateWithCredential(auth.currentUser, credential);
      return { success: true };
    } catch (err: any) {
      let msg = 'Authentication verification failed. Please check your password.';
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        msg = 'Incorrect password. Action cancelled for security.';
      }
      return { success: false, error: msg };
    }
  };

  const updateUserProfile = async (updates: Partial<UserProfile>) => {
    if (!userProfile) {
      return { success: false, error: 'No active profile found.' };
    }

    try {
      const updatedProfile: UserProfile = {
        ...userProfile,
        ...updates,
        updatedAt: new Date().toISOString(),
      };

      if (auth.currentUser) {
        if (updates.fullName) {
          try {
            await firebaseUpdateProfile(auth.currentUser, { displayName: updates.fullName });
          } catch {
            // ignore
          }
        }
        await updateUserProfileInFirestore(userProfile.id, updates);
      }

      setUserProfile(updatedProfile);
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to update profile.' };
    }
  };

  const sendVerificationEmail = async (): Promise<{ success: boolean; message: string; error?: string }> => {
    if (!userProfile && !auth.currentUser) {
      return { success: false, message: '', error: 'You must be signed in to send a verification email.' };
    }

    const targetEmail = userProfile?.email || auth.currentUser?.email || '';

    try {
      if (auth.currentUser) {
        const origin = typeof window !== 'undefined' ? window.location.origin : 'https://cameroon-land-market-ef9fd.web.app';
        const actionCodeSettings = {
          url: `${origin}/auth/action`,
          handleCodeInApp: true,
        };
        await sendEmailVerification(auth.currentUser, actionCodeSettings);
      }
      return {
        success: true,
        message: `Verification email sent to ${targetEmail}. Please check your inbox or spam folder.`,
      };
    } catch (err: any) {
      console.warn('Firebase sendEmailVerification notice:', err?.code, err?.message);
      return {
        success: false,
        message: '',
        error: err?.message || 'Failed to dispatch verification email.',
      };
    }
  };

  const confirmEmailVerification = async (): Promise<{ success: boolean; error?: string }> => {
    if (!auth.currentUser) {
      return { success: false, error: 'No active session found.' };
    }

    try {
      await auth.currentUser.reload();
      const verified = Boolean(auth.currentUser.emailVerified);
      setIsEmailVerified(verified);
      if (!verified) {
        return {
          success: false,
          error: 'Email has not been verified yet. Please check your inbox and click the verification link.',
        };
      }
      setUserProfile((prev) => (prev ? { ...prev, isVerified: true } : prev));
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to check verification status.' };
    }
  };

  const resendVerificationEmail = async (
    targetEmailArg?: string,
    passwordArg?: string
  ): Promise<{ success: boolean; message: string; error?: string }> => {
    // Rate limit check: at most 1 resend per 60 seconds per email
    const now = Date.now();
    const lastSentStr = sessionStorage.getItem('clm_last_resend_ts');
    if (lastSentStr) {
      const elapsed = Math.floor((now - parseInt(lastSentStr, 10)) / 1000);
      if (elapsed < 60) {
        const remaining = 60 - elapsed;
        return {
          success: false,
          message: '',
          error: `Please wait ${remaining} seconds before requesting another verification email.`,
        };
      }
    }

    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://cameroon-land-market-ef9fd.web.app';
    const actionSettings = {
      url: `${origin}/auth/action`,
      handleCodeInApp: true,
    };

    // Case 1: Active session present
    if (auth.currentUser) {
      try {
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          setIsEmailVerified(true);
          setUserProfile((prev) => (prev ? { ...prev, isVerified: true } : prev));
          return { success: true, message: 'Your email is already verified!' };
        }
      } catch {}

      try {
        await sendEmailVerification(auth.currentUser, actionSettings);
        sessionStorage.setItem('clm_last_resend_ts', now.toString());
        return {
          success: true,
          message: `A new verification link has been sent to ${auth.currentUser.email}. Please check your inbox and spam folder.`,
        };
      } catch (err: any) {
        if (err.code === 'auth/too-many-requests') {
          return { success: false, message: '', error: 'Too many requests sent. Please wait a few minutes before trying again.' };
        }
        return { success: false, message: '', error: err.message || 'Failed to resend verification link.' };
      }
    }

    // Case 2: User is signed out (provide email + password to authenticate temporarily, send, and immediately sign out)
    if (targetEmailArg && passwordArg) {
      try {
        const cred = await signInWithEmailAndPassword(auth, targetEmailArg.trim().toLowerCase(), passwordArg);
        if (cred.user.emailVerified) {
          try { await firebaseSignOut(auth); } catch {}
          return { success: true, message: 'This account is already verified! You can sign in directly.' };
        }

        await sendEmailVerification(cred.user, actionSettings);
        // Sign back out immediately to maintain mandatory unverified quarantine
        try { await firebaseSignOut(auth); } catch {}
        sessionStorage.setItem('clm_last_resend_ts', now.toString());

        return {
          success: true,
          message: `A fresh verification link has been dispatched to ${targetEmailArg}. Please verify your email before logging in.`,
        };
      } catch (err: any) {
        try { await firebaseSignOut(auth); } catch {}
        if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
          return { success: false, message: '', error: 'Incorrect password. Cannot resend verification link.' };
        }
        if (err.code === 'auth/too-many-requests') {
          return { success: false, message: '', error: 'Too many requests. Please wait a few minutes before trying again.' };
        }
        return { success: false, message: '', error: err.message || 'Unable to resend verification email.' };
      }
    }

    return {
      success: false,
      message: '',
      error: 'Please specify your email and password to receive a new verification link.',
    };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        loading,
        isAuthenticated: Boolean(user),
        isSuperAdmin,
        signUp,
        signIn,
        signInWithGoogle,
        signInWithFacebook,
        signInWithApple,
        signInWithMicrosoft,
        signInWithTwitter,
        signInWithSocial,
        verifyAdminPrivilege,
        signInAsRole,
        signOut,
        sendPasswordReset,
        updateUserPassword,
        updateUserProfile,
        sendVerificationEmail,
        confirmEmailVerification,
        reauthenticateCurrentUser,
        isEmailVerified,
        resendVerificationEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
